import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse, HttpContext, HttpContextToken } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { TokenService } from '@services/token.service';
import { AuthService } from '@services/auth.service';
import { Router } from '@angular/router';
import { OrderService } from '@services/order.service';

const SEND_TOKEN = new HttpContextToken<boolean>(() => true);
export function notSendToken() {
  return new HttpContext().set(SEND_TOKEN, false);
}

export const refreshTokenInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const ordersService = inject(OrderService);

  let isRefreshing = false;
  const refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  if (!req.context.get(SEND_TOKEN)) {
    return next(req);
  }

  function addTokenHeader(request: HttpRequest<any>, access_token: string) {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${access_token}`),
    });
  }

  function handle401Error(request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);
      const refreshToken = tokenService.getRefreshToken();
      return authService.refreshToken({ refresh_token: refreshToken }).pipe(
        switchMap(({ data }) => {
          const { jwt } = data[0];
          isRefreshing = false;
          tokenService.saveToken(jwt);
          refreshTokenSubject.next(jwt);
          return next(addTokenHeader(request, jwt));
        }),
        catchError(async (err) => {
          isRefreshing = false;
          tokenService.signOut();
          await router.navigate(['']);
          ordersService.refreshHome$.emit(true);
          window.location.reload();
          return throwError(() => err);
        })
      );
    }
    return refreshTokenSubject.pipe(
      filter((access_token) => access_token !== null),
      take(1),
      switchMap((access_token) => next(addTokenHeader(request, access_token)))
    );
  }

  let authReq = req;
  const token = tokenService.getToken();
  if (token) {
    authReq = addTokenHeader(req, token);
  }

  return next(authReq).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        token
      ) {
        return handle401Error(authReq, next);
      }
      return throwError(() => error);
    })
  );
};
